import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, VerifyData, ConvertSaleOrder, ConvertSaleOrderVerify } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, Refresh, Warning, CheckCircle, Cancel, Close, Search
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Grid,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery
} from '@mui/material';
import SaleOrderModal from './SaleOrderModal';
import SaleModal from '../Sale/SaleModal';
import Pagination from '../../common/Pagination';
import './SaleOrderIndex.css';

const SaleOrderIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [SaleOrders, setSaleOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [SaleOrderDetails, setSaleOrderDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Convert confirmation modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertItemId, setConvertItemId] = useState(null);
  const [convertItemNo, setConvertItemNo] = useState('');

  // Stock warning modal
  const [stockWarningOpen, setStockWarningOpen] = useState(false);
  const [stockWarnings, setStockWarnings] = useState([]);
  const [pendingSaleData, setPendingSaleData] = useState(null); // holds prepared data while user reviews stock warning

  // SaleModal (convert to sale) state
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [saleFormData, setSaleFormData] = useState({});
  const [saleDetails, setSaleDetails] = useState([]);
  const [saleBundleDetails, setSaleBundleDetails] = useState([]);
  const [saleStockBranchDetails, setSaleStockBranchDetails] = useState([]);
  const [saleCustomers, setSaleCustomers] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [saleBanks, setSaleBanks] = useState([]);
  const [saleFreightPackingGST, setSaleFreightPackingGST] = useState({ freight: null, packing: null });
  const [saleCompanyData, setSaleCompanyData] = useState(null);

  // Details section state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData(fromDate, toDate);
    }
  }, [companyId, token, fromDate, toDate]);

  // Force re-render when SaleOrders change
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    console.log('SaleOrders state changed:', SaleOrders);
    setRenderKey(prev => prev + 1);
  }, [SaleOrders]);

  // Fetch functions
  const fetchSaleOrders = async (fromDateParam = '', toDateParam = '') => {
    try {
      let endpoint = `/${companyId}/${branchId}/`;

      // Add date range parameters if provided
      if (fromDateParam && toDateParam) {
        endpoint += `${fromDateParam}/${toDateParam}`;
      } else if (fromDateParam) {
        endpoint += `${fromDateParam}`;
      } else if (toDateParam) {
        endpoint += `/${toDateParam}`;
      }

      console.log('Fetching Sale orders from endpoint:', endpoint);
      const response = await fetchData('SaleOrderApi', endpoint, token);
      console.log('Raw API response:', response);

      // Handle different response structures
      let SaleOrderData = [];
      if (response) {
        if (response.response_data) {
          SaleOrderData = Array.isArray(response.response_data) ? response.response_data : [];
        } else if (Array.isArray(response)) {
          SaleOrderData = response;
        } else if (response.data) {
          SaleOrderData = Array.isArray(response.data) ? response.data : [];
        } else if (response.SaleOrders) {
          SaleOrderData = Array.isArray(response.SaleOrders) ? response.SaleOrders : [];
        }
      }

      console.log('Processed Sale order data:', SaleOrderData);
      console.log('Number of Sale orders:', SaleOrderData.length);

      // Force state update with callback to ensure re-render
      setSaleOrders(prevSaleOrders => {
        console.log('Previous Sale orders:', prevSaleOrders);
        console.log('Setting new Sale orders:', SaleOrderData);
        return [...SaleOrderData]; // Create new array reference
      });
    } catch (error) {
      console.error('Error fetching Sale orders:', error);
      setSaleOrders([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('SaleOrderApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxBillNo Data:', data);

      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || 'SO';
      const finYear = data.FinYear || data.finYear || '';

      // Generate SaleOrderNo based on available data
      let SaleOrderNo = '';
      if (billPrefix && finYear) {
        SaleOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        SaleOrderNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        SaleOrderNo = `${billPrefix}/${billNumber}`;
      } else {
        SaleOrderNo = `${billNumber}`;
      }

      return { billNumber, billPrefix, finYear, SaleOrderNo };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: 'SO', finYear: '', SaleOrderNo: 'SO/1' };
    }
  };

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Freight + Packing GST
  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      console.log('AccountHeadFreightAndPacking API Response:', response);
      const data = response.response_data || response || [];
      console.log('AccountHeadFreightAndPacking Data:', data);

      // Find Freight and Packing entries from the response
      let freightData = null;
      let packingData = null;

      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight') || item.AccountHeadName?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing') || item.AccountHeadName?.toLowerCase().includes('packing'));
      } else if (data) {
        // If it's an object with Freight and Packing properties
        freightData = data.Freight || data.FreightCharge || null;
        packingData = data.Packing || data.PackingCharge || null;
      }

      setFreightPackingGST({
        freight: freightData,
        packing: packingData
      });

      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchAllData = async (fromDateParam = '', toDateParam = '') => {
    setLoading(true);
    setLoadingMessage('Fetching Sale orders...');
    await Promise.allSettled([
      fetchSaleOrders(fromDateParam, toDateParam),
      fetchCustomers(),
      fetchItems(),
      fetchFreightAndPackingGST()
    ]);
    setLoading(false);
  };

  const fetchSingleSaleOrder = async (id) => {
    try {
      const response = await fetchData('SaleOrderApi', `/${id}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching Sale order details:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    if (action === 'view') {
      const recordId = typeof itemOrId === 'object' ? (itemOrId?.Id || 0) : (itemOrId || 0);
      logAudit(AUDIT_ACTION.VIEW, 'SaleOrderApi', recordId);
    }
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);

      const freshData = await fetchSingleSaleOrder(itemId);

      if (freshData) {
        const SaleOrderData = freshData.SaleOrder || freshData;
        const details = freshData.SaleOrderDetails || freshData.Details || [];

        setSelectedItem(SaleOrderData);
        setFormData({
          Id: SaleOrderData.Id,
          CompanyId: SaleOrderData.CompanyId || companyId,
          BranchId: SaleOrderData.BranchId || 0,
          MasterId: SaleOrderData.MasterId,
          InvoiceTypeId: SaleOrderData.InvoiceTypeId || 0,
          InvoiceTypeName: SaleOrderData.InvoiceTypeName || customers.find(c => (c.MasterId || c.Id) === SaleOrderData.MasterId)?.InvoiceTypeName || '',
          SaleOrderNo: SaleOrderData.SaleOrderNo,
          SaleOrderNumber: SaleOrderData.SaleOrderNumber || 0,
          SaleOrderPrefix: SaleOrderData.SaleOrderPrefix || '',
          SaleOrderDate: SaleOrderData.SaleOrderDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          FinYear: SaleOrderData.FinYear || '',
          Instruction1: SaleOrderData.Instruction1 || '',
          Instruction2: SaleOrderData.Instruction2 || '',
          Remarks: SaleOrderData.Remarks || '',
          Amount: SaleOrderData.Amount || 0,
          DiscountAmount: SaleOrderData.DiscountAmount || 0,
          TaxableAmount: SaleOrderData.TaxableAmount || 0,
          CGSTAmount: SaleOrderData.CGSTAmount || 0,
          SGSTAmount: SaleOrderData.SGSTAmount || 0,
          IGSTAmount: SaleOrderData.IGSTAmount || 0,
          FreightAmount: SaleOrderData.FreightAmount || 0,
          FreightGSTRate: SaleOrderData.FreightGSTRate || 0,
          FreightGSTAmount: SaleOrderData.FreightGSTAmount || 0,
          PackingAmount: SaleOrderData.PackingAmount || SaleOrderData.PackingCharge || 0,
          PackingGSTRate: SaleOrderData.PackingGSTRate || SaleOrderData.PackingChargeGSTRate || 0,
          PackingGSTAmount: SaleOrderData.PackingGSTAmount || SaleOrderData.PackingChargeGSTAmount || 0,
          RoundOff: SaleOrderData.RoundOff || 0,
          TotalAmount: SaleOrderData.TotalAmount || 0,
          IsConverted: SaleOrderData.IsConverted || false,
          IsVerified: SaleOrderData.IsVerified || false,
          IsActive: SaleOrderData.IsActive ?? true,
          CreatedBy: SaleOrderData.CreatedBy || null,
          CreatedDate: SaleOrderData.CreatedDate || SaleOrderData.CreatedOn || null
        });
        setSaleOrderDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy || null,
          CreatedDate: d.CreatedDate || d.CreatedOn || null
        })));
      } else {
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
        setSaleOrderDetails([]);
      }

      setModalLoading(false);
    } else {
      // Create mode - fetch max bill number and freight/packing GST
      setModalLoading(true);

      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();

      // Get GST rates from API data
      const freightGSTRate = gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: '',
        InvoiceTypeId: 0,
        InvoiceTypeName: '',
        SaleOrderNumber: maxBillData.billNumber,
        SaleOrderNo: maxBillData.SaleOrderNo,
        SaleOrderPrefix: maxBillData.billPrefix,
        SaleOrderDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        IsConverted: false,
        IsVerified: false,
        IsActive: true
      });
      setSaleOrderDetails([]);
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setSaleOrderDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} Sale order...`);

      const totalQuantity = SaleOrderDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0);

      const dataToSend = {
        SaleOrder: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || 0,
          MasterId: parseInt(formData.MasterId) || 0,
          InvoiceTypeId: formData.InvoiceTypeId || 0,
          SaleOrderNo: formData.SaleOrderNo || '',
          SaleOrderNumber: formData.SaleOrderNumber || 0,
          SaleOrderPrefix: formData.SaleOrderPrefix || '',
          SaleOrderDate: formData.SaleOrderDate || new Date().toISOString().split('T')[0],
          FinYear: formData.FinYear || '',
          Instruction1: formData.Instruction1 || '',
          Instruction2: formData.Instruction2 || '',
          Remarks: formData.Remarks || '',
          TotalQuantity: totalQuantity,
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingCharge: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingChargeGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          PackingChargeGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          RoundOff: parseFloat((parseFloat(formData.RoundOff) || 0).toFixed(2)),
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          IsConverted: formData.IsConverted || false,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        SaleOrderDetails: SaleOrderDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          SaleOrderId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          GSTRate: parseFloat(detail.GSTRate) || 0,
          Quantity: parseFloat(detail.Quantity) || 0,
          Rate: parseFloat(detail.Rate) || 0,
          Amount: parseFloat(detail.Amount) || 0,
          DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
          TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
          CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
          SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
          IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
          TotalAmount: parseFloat(detail.TotalAmount) || 0,
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          CreatedDate: modalAction === 'edit' ? detail.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        }))
      };

      if (modalAction === 'create') {
        await postData('SaleOrderApi', '', dataToSend, token);
        setSuccess('Sale order created successfully!');
        logAudit(AUDIT_ACTION.CREATE, 'SaleOrderApi', 0, null, { BillNumber: formData.BillNumber || '', CustomerName: formData.CustomerName || '' });
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Sale Order ID is missing');
        await updateData('SaleOrderApi', formData.Id, dataToSend, token);
        setSuccess('Sale order updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, 'SaleOrderApi', formData.Id, { ...selectedItem }, { ...formData });
      }

      handleCloseModal();
      await fetchSaleOrders(fromDate, toDate);
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} Sale order. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, SaleOrderNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(SaleOrderNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('SaleOrderApi', deleteItemId, token);
      setSuccess('Sale order deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, 'SaleOrderApi', deleteItemId, { Id: deleteItemId }, null);
      await fetchSaleOrders(fromDate, toDate);
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete Sale order.');
    }
    setLoading(false);
  };

  // Details section handlers
  const handleOpenDetails = async (item) => {
    setModalLoading(true);
    const freshData = await fetchSingleSaleOrder(item.Id);

    if (freshData) {
      const SaleOrderData = freshData.SaleOrder || freshData;
      const details = freshData.SaleOrderDetails || freshData.Details || [];

      setSelectedDetails({
        ...SaleOrderData,
        Details: details,
        CustomerName: item.CustomerName || customers.find(c => c.MasterId === SaleOrderData.MasterId)?.Name || '-'
      });
      setDetailsOpen(true);
    }
    setModalLoading(false);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedDetails(null);
  };

  // Verify handler
  const handleVerifySaleOrder = async (SaleOrderId, SaleOrderNo) => {
    if (!SaleOrderId) return;

    setLoading(true);
    setLoadingMessage('Verifying Sale order...');

    try {
      const response = await ConvertSaleOrderVerify('SaleOrderApi', SaleOrderId, token);

      if (response && response.success !== false) {
        setSuccess(`Sale order ${SaleOrderNo} verified successfully!`);
        logAudit(AUDIT_ACTION.VERIFY, 'SaleOrderApi', SaleOrderId);
        // Refresh the Sale orders list to show updated verification status
        await fetchSaleOrders(fromDate, toDate);
        // Close the modal after successful verification
        handleCloseModal();
      } else {
        throw new Error(response?.message || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying Sale order:', error);
      setError(`Failed to verify Sale order ${SaleOrderNo}. ${error.message || 'Please try again.'}`);
    }

    setLoading(false);
  };

  // Convert handler
  const handleOpenConvertModal = (id, soNo) => {
    setConvertItemId(id);
    setConvertItemNo(soNo);
    setConvertModalOpen(true);
  };

  const handleCloseConvertModal = () => {
    setConvertModalOpen(false);
    setConvertItemId(null);
    setConvertItemNo('');
  };

  // Convert handler
  const handleConvertToSale = async () => {
    if (!convertItemId) return;
    handleCloseConvertModal();
    handleCloseModal();
    setLoading(true);
    setLoadingMessage('Preparing sale from sale order...');
    try {
      // 1. Fetch the SaleOrder details (with ItemName via SaleApi)
      const soRaw = await fetchData('SaleApi', `/SaleOrderDetails/${companyId}/${convertItemId}`, token);
      const soData = soRaw?.response_data || soRaw;
      // const soHeader = soData?.SaleOrder || soData;
      // const soDetails = soData?.SaleOrderDetails || soData?.Details || [];
      const soHeader = soData?.Sale || soData;
      const soDetails = soData?.SaleDetails || soData?.Details || [];

      // 2. Fetch Sale supporting data in parallel — resolve customer & invoice type first
      const [custRes2, itemRes, bankRes, gstRes, companyRes, invoiceTypeRes] = await Promise.all([
        fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token),
        fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/${soHeader.MasterId || 0}`, token),
        fetchData('BankApi', `/${companyId}`, token),
        fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token),
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token),
        fetchData('InvoiceTypeApi', `/${companyId}`, token),
      ]);

      const custList       = custRes2.response_data || custRes2 || [];
      const itemList       = itemRes.response_data  || itemRes  || [];
      const bankList       = bankRes.response_data  || bankRes  || [];
      const gstList        = Array.isArray(gstRes.response_data || gstRes) ? (gstRes.response_data || gstRes) : [];
      const companyInfo    = companyRes.response_data || companyRes || null;
      const invoiceTypes   = invoiceTypeRes.response_data || invoiceTypeRes || [];

      const resolvedCustomer = custList.find(c => (c.MasterId || c.Id) === soHeader.MasterId);
      const customerName = soHeader.CustomerName || resolvedCustomer?.Name || '';
      const gstNumber = resolvedCustomer?.GSTNumber || resolvedCustomer?.GSTNo || '';
      const invoiceTypeName = resolvedCustomer?.InvoiceTypeName || resolvedCustomer?.InvoiceType || '';

      // Resolve InvoiceTypeId: prefer SaleOrder header value, then match by name from InvoiceTypes list,
      // then fall back to customer's InvoiceTypeId field
      const resolvedInvoiceType = invoiceTypes.find(
        it => it.Name === invoiceTypeName || it.Id === resolvedCustomer?.InvoiceTypeId
      );
      const invoiceTypeId = soHeader.InvoiceTypeId
        || resolvedInvoiceType?.Id
        || resolvedCustomer?.InvoiceTypeId
        || 0;

      // Fetch bill number with correct invoice type
      const invoiceTypeParam = invoiceTypeId ? `/${invoiceTypeId}` : '';
      const billRes = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId || 1}${invoiceTypeParam}`, token);
      const billData = billRes.response_data || billRes || {};

      const freightData = gstList.find(i => i.Name?.toLowerCase().includes('freight')) || null;
      const packingData = gstList.find(i => i.Name?.toLowerCase().includes('packing')) || null;

      setSaleCustomers(custList);
      setSaleItems(itemList);
      setSaleBanks(bankList);
      setSaleFreightPackingGST({ freight: freightData, packing: packingData });
      setSaleCompanyData(companyInfo);

      // 3. Build the sale form data object (used in both paths below)
      const preparedFormData = {
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: soHeader.MasterId || '',
        CustomerName: customerName,
        GSTNumber: gstNumber,
        InvoiceTypeId: invoiceTypeId || billData.InvoiceTypeId || 0,
        InvoiceTypeName: invoiceTypeName,
        BillNumber: billData.BillNumber ?? billData.billNumber ?? '',
        BillPrefix: billData.BillPrefix || billData.billPrefix || '',
        FinYear: billData.FinYear || billData.finYear || '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        GstCalculationType: soHeader.GstCalculationType || 1,
        GstCalculationTypeName: soHeader.GstCalculationTypeName || 'Excluding Tax',
        Amount: soHeader.Amount || 0,
        DiscountAmount: soHeader.DiscountAmount || 0,
        TaxableAmount: soHeader.TaxableAmount || 0,
        CGSTAmount: soHeader.CGSTAmount || 0,
        SGSTAmount: soHeader.SGSTAmount || 0,
        IGSTAmount: soHeader.IGSTAmount || 0,
        FreightAmount: soHeader.FreightAmount || 0,
        FreightGSTRate: soHeader.FreightGSTRate || parseFloat(freightData?.GSTRate) || 0,
        FreightGSTAmount: soHeader.FreightGSTAmount || 0,
        PackingAmount: soHeader.PackingAmount || 0,
        PackingGSTRate: soHeader.PackingGSTRate || parseFloat(packingData?.GSTRate) || 0,
        PackingGSTAmount: soHeader.PackingGSTAmount || 0,
        TCSAmount: 0,
        RoundOff: soHeader.RoundOff || 0,
        TotalAmount: soHeader.TotalAmount || 0,
        GrandTotal: soHeader.TotalAmount || 0,
        CashAmount: 0,
        UPIAmount: 0,
        CardAmount: 0,
        BankId: null,
        BalanceAmount: soHeader.TotalAmount || 0,
        Remarks: soHeader.Remarks || '',
        DispatchFrom: '', DispatchAddress1: '', DispatchAddress2: '',
        DispatchLocation: '', DispatchPincode: '',
        ShipTo: '', ShipGSTNo: '', ShipToAddress1: '', ShipToAddress2: '',
        ShipToLocation: '', ShipToPincode: '',
        VehicleNumber: '', TransporterName: '', TransporterGST: '', Distance: 0,
        IsVerified: false, IsActive: true,
        // carry SaleOrderId for reference
        SaleOrderId: convertItemId,
      };

      // 4. Map SaleOrder details → Sale details (resolve ItemName from API response or fetched itemList)
      const mappedSaleDetails = soDetails.map((d, idx) => {
        const freshItem = itemList.find(i => i.Id === parseInt(d.ItemId));
        return {
          Id: 0,
          _rowKey: `row_${Date.now()}_${Math.random()}_${idx}`, // required for Branch Split Modal
          _rowIndex: idx,
          ItemId: d.ItemId,
          ItemName: d.ItemName || freshItem?.Name || '',
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          MinRate: freshItem?.MinRate || 0,
          MaxRate: freshItem?.MaxRate || 0,
          HasWarranty: freshItem?.HasWarranty || false,
          IsInstallation: false,
          // Use CurrentBranchStock first (login-branch stock), fall back to BalanceStock
          BalanceStock: parseFloat(freshItem?.CurrentBranchStock ?? freshItem?.BalanceStock) || 0,
          AssignedBranchId: parseInt(branchId) || null, // default to login branch for branch split
          BatchNumber: '',
          BatchId: 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          BilledQuantity: d.Quantity || 0,
          OrderQuantity: d.Quantity || 0,
          StockQuantity: d.Quantity || 0,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          IsActive: true,
        };
      });

      // 5. Stock check — compare ordered qty against CurrentBranchStock
      //    Also surface a branch-split hint when company has stock but this branch doesn't
      const insufficientItems = soDetails
        .map(d => {
          const itemData = itemList.find(i => i.Id === parseInt(d.ItemId));
          // Skip service items — they carry no physical stock
          if (itemData?.IsService) return null;
          const available = parseFloat(itemData?.CurrentBranchStock ?? itemData?.BalanceStock) || 0;
          const companyStock = parseFloat(itemData?.CompanyStock) || 0;
          const ordered = parseFloat(d.Quantity) || 0;
          if (ordered > available) {
            return {
              name: d.ItemName || itemData?.Name || `Item #${d.ItemId}`,
              ordered,
              available,
              shortage: ordered - available,
              // hint: other branches may cover the gap
              hasBranchStock: companyStock > available,
            };
          }
          return null;
        })
        .filter(Boolean);

      setSaleDetails(mappedSaleDetails);

      if (insufficientItems.length > 0) {
        // Store prepared data and show stock warning — user can still proceed or cancel
        setPendingSaleData({ saleFormData: preparedFormData });
        setStockWarnings(insufficientItems);
        setStockWarningOpen(true);
        setLoading(false);
        return;
      }

      // No stock issues — open sale modal directly
      setSaleFormData(preparedFormData);

      setSaleModalOpen(true);
    } catch (error) {
      console.error('Error preparing sale from sale order:', error);
      setError(`Failed to prepare sale: ${error.response?.data?.message || error.message}`);
    }
    setLoading(false);
  };

  const handleCloseSaleModal = () => {
    setSaleModalOpen(false);
    setSaleFormData({});
    setSaleDetails([]);
    setSaleBundleDetails([]);
    setSaleStockBranchDetails([]);
    setSaleCompanyData(null);
  };

  // Stock warning dialog handlers
  const handleStockWarningCancel = () => {
    setStockWarningOpen(false);
    setStockWarnings([]);
    setPendingSaleData(null);
    setSaleDetails([]);
    setSaleBundleDetails([]);
    setSaleStockBranchDetails([]);
  };

  const handleStockWarningProceed = () => {
    if (pendingSaleData) {
      setSaleFormData(pendingSaleData.saleFormData);
    }
    setStockWarningOpen(false);
    setStockWarnings([]);
    setPendingSaleData(null);
    setSaleModalOpen(true);
  };

  const handleSaleCustomerChange = async (masterId) => {
    if (!masterId) return;
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/${masterId}`, token);
      setSaleItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items for customer:', error);
    }
  };

  const handleSaveSale = async () => {
    setLoading(true);
    setLoadingMessage('Creating sale...');
    try {
      const dataToSend = {
        Sale: {
          Id: 0,
          CompanyId: companyId,
          BranchId: saleFormData.BranchId || branchId,
          MasterId: parseInt(saleFormData.MasterId) || 0,
          CustomerName: saleFormData.CustomerName || '',
          GSTNumber: saleFormData.GSTNumber || '',
          SalesManId: saleFormData.SalesManId || null,
          StateId: saleFormData.StateId || null,
          InvoiceTypeId: saleFormData.InvoiceTypeId || 0,
          BillNumber: saleFormData.BillNumber || '',
          BillPrefix: saleFormData.BillPrefix || '',
          FinYear: saleFormData.FinYear || '',
          BillDate: saleFormData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: saleFormData.ClearingDate || new Date().toISOString().split('T')[0],
          GstCalculationType: saleFormData.GstCalculationType || 1,
          GstCalculationTypeName: saleFormData.GstCalculationTypeName || 'Excluding Tax',
          Amount: parseFloat(saleFormData.Amount) || 0,
          DiscountAmount: parseFloat(saleFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(saleFormData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(saleFormData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(saleFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(saleFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(saleFormData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(saleFormData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(saleFormData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(saleFormData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(saleFormData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(saleFormData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(saleFormData.TCSAmount) || 0,
          RoundOff: parseFloat(saleFormData.RoundOff) || 0,
          TotalAmount: parseFloat(saleFormData.TotalAmount) || 0,
          GrandTotal: parseFloat(saleFormData.TotalAmount) || 0,
          CashAmount: parseFloat(saleFormData.CashAmount) || 0,
          UPIAmount: parseFloat(saleFormData.UPIAmount) || 0,
          CardAmount: parseFloat(saleFormData.CardAmount) || 0,
          BankId: saleFormData.BankId || null,
          BalanceAmount: parseFloat(saleFormData.BalanceAmount) || 0,
          Remarks: saleFormData.Remarks || '',
          DispatchFrom: saleFormData.DispatchFrom || '',
          DispatchAddress1: saleFormData.DispatchAddress1 || '',
          DispatchAddress2: saleFormData.DispatchAddress2 || '',
          DispatchLocation: saleFormData.DispatchLocation || '',
          DispatchPincode: saleFormData.DispatchPincode || '',
          ShipTo: saleFormData.ShipTo || '',
          ShipGSTNo: saleFormData.ShipGSTNo || '',
          ShipToAddress1: saleFormData.ShipToAddress1 || '',
          ShipToAddress2: saleFormData.ShipToAddress2 || '',
          ShipToLocation: saleFormData.ShipToLocation || '',
          ShipToPincode: saleFormData.ShipToPincode || '',
          VehicleNumber: saleFormData.VehicleNumber || '',
          TransporterName: saleFormData.TransporterName || '',
          TransporterGST: saleFormData.TransporterGST || '',
          Distance: parseInt(saleFormData.Distance) || 0,
          IsVerified: false,
          IsHold: saleFormData.IsHold || false,
          IsActive: true,
          SaleOrderId: convertItemId || saleFormData.SaleOrderId || null,
          CreatedBy: userId,
        },
        SaleDetails: saleDetails.map(d => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: saleFormData.BranchId || branchId || 1,
          SaleId: 0,
          ItemId: parseInt(d.ItemId) || 0,
          ItemName: d.ItemName || '',
          HSNCode: d.HSNCode || '',
          GSTRate: parseFloat(d.GSTRate) || 0,
          Quantity: parseFloat(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.DiscountAmount) || 0,
          TaxableAmount: parseFloat(d.TaxableAmount) || 0,
          CGSTAmount: parseFloat(d.CGSTAmount) || 0,
          SGSTAmount: parseFloat(d.SGSTAmount) || 0,
          IGSTAmount: parseFloat(d.IGSTAmount) || 0,
          TotalAmount: parseFloat(d.TotalAmount) || 0,
          BatchId: parseInt(d.BatchId) || 0,
          BatchNumber: d.BatchNumber || '',
          IsInstallation: d.IsInstallation ?? false,
          BalanceStock: parseFloat(d.BalanceStock) || 0,
          IsActive: true,
          CreatedBy: userId,
        })),
        SaleBundleDetails: saleBundleDetails
          .filter(b => b._parentItemId)
          .map(b => {
            const parentDetail = saleDetails[b._parentRowIndex];
            return {
              Id: 0,
              SaleId: 0,
              ItemId: parseInt(parentDetail?.ItemId) || 0,
              ProductItemId: parseInt(b.ProductItemId) || parseInt(b.ItemId) || null,
              Quantity: parseFloat(b.Quantity) || 0,
              Rate: parseFloat(b.Rate) || 0,
              Amount: parseFloat(b.Amount) || 0,
              IsActive: b.IsActive ?? true,
              CreatedBy: userId,
            };
          }),
        // Build SaleStockBranchDetails: honour branch splits set via SaleModal's Branch Split modal,
        // or fall back to a single entry for the login branch (same logic as SaleIndex).
        SaleStockBranchDetails: saleDetails.flatMap(detail => {
          if (detail._branchSplits && detail._branchSplits.length > 0) {
            // Item was split across branches in the Branch Split modal
            return detail._branchSplits.map(split => ({
              Id: 0,
              SaleId: 0,
              SaleDetailsId: 0,
              BranchId: split.branchId || parseInt(branchId) || null,
              ItemId: parseInt(detail.ItemId) || 0,
              Quantity: split.qty || 0,
              IsActive: detail.IsActive ?? true,
              CreatedBy: userId,
            }));
          }
          // Default: assign entire quantity to login branch
          return [{
            Id: 0,
            SaleId: 0,
            SaleDetailsId: 0,
            BranchId: parseInt(detail.AssignedBranchId) || parseInt(branchId) || null,
            ItemId: parseInt(detail.ItemId) || 0,
            Quantity: parseFloat(detail.Quantity) || 0,
            IsActive: detail.IsActive ?? true,
            CreatedBy: userId,
          }];
        }),
      };

      await postData('SaleApi', '', dataToSend, token);
      // Mark SaleOrder as converted
      await ConvertSaleOrder('SaleOrderApi', convertItemId || saleFormData.SaleOrderId, token);
      setSuccess(`Sale created successfully from Sale Order ${convertItemNo}!`);
      logAudit(AUDIT_ACTION.APPROVE, 'SaleOrderApi', convertItemId || saleFormData.SaleOrderId);
      handleCloseSaleModal();
      await fetchSaleOrders(fromDate, toDate);
    } catch (error) {
      console.error('Error saving sale:', error);
      setError(`Failed to create sale: ${error.response?.data?.message || error.message}`);
    }
    setLoading(false);
  };


  // Filter and pagination
  const getFilteredData = (data) => {
    console.log('getFilteredData input:', data);

    if (!Array.isArray(data)) {
      console.warn('getFilteredData received non-array data:', typeof data, data);
      return [];
    }

    let filtered = [...data]; // Create a copy to avoid mutations

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.SaleOrderNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customers.find(c => c.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    console.log('getFilteredData output:', filtered);
    return filtered;
  };

  const getPaginatedData = (data) => {
    console.log('getPaginatedData input:', data);

    const filtered = getFilteredData(data) || [];
    console.log('getPaginatedData filtered:', filtered);

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    const paginatedResult = {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };

    console.log('getPaginatedData result:', paginatedResult);
    return paginatedResult;
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData(fromDate, toDate);
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(SaleOrders);

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Sale Order</h4>
        </div>

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Sale Order" arrow>
                  <IconButton onClick={() => handleOpenModal('create')} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                    <Add sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Sale Order No, Customer..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Sale Order No, Customer..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Sale Order" arrow>
              <Button variant="contained" onClick={() => handleOpenModal('create')} sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Sale Order</span>
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Sale Order No.', 'Date', 'Customer', 'Total Amount', 'Verified', 'Converted', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 8 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id || index} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <ShoppingCart sx={{ fontSize: 16, color: '#2C3E6E' }} />
                          {item.SaleOrderNo || '-'}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.SaleOrderDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.CustomerName || customers.find(c => c.MasterId === item.MasterId)?.Name || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>{formatCurrency(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsVerified ? 'Verified' : 'Pending'} color={item.IsVerified ? 'success' : 'warning'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsConverted ? 'Converted' : 'Open'} color={item.IsConverted ? 'info' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Tooltip title="View" arrow>
                            <Button size="small" onClick={() => handleOpenModal('view', item.Id)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                          </Tooltip>
                          <Tooltip title={item.IsVerified ? 'Cannot edit verified order' : 'Edit'} arrow>
                            <span>
                              <Button size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsConverted || item.IsVerified} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                            </span>
                          </Tooltip>
                          <Tooltip title={item.IsVerified ? 'Cannot delete verified order' : 'Delete'} arrow>
                            <span>
                              <Button size="small" onClick={() => handleOpenDeleteModal(item.Id, item.SaleOrderNo)} disabled={item.IsConverted || item.IsVerified} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                            </span>
                          </Tooltip>
                          {item.IsVerified && !item.IsConverted && (
                            <Tooltip title="Convert to Sale" arrow>
                              <Button
                                size="small"
                                onClick={() => handleOpenConvertModal(item.Id, item.SaleOrderNo)}
                                style={{ minWidth: '60px', height: '22px', borderRadius: '6px', padding: '2px 6px', fontSize: '10px', background: '#e67e22', color: 'white', fontWeight: 700 }}
                              >
                                SO To Sale 
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '13px' }}>
                      {loading ? 'Loading Sale orders...' : Array.isArray(SaleOrders) ? (SaleOrders.length === 0 ? 'No Sale orders found' : 'No Sale orders match current filters') : 'Error loading Sale orders'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={rowsPerPage}
          onItemsPerPageChange={(v) => { setRowsPerPage(Number(v)); setCurrentPage(1); }}
          totalItems={totalCount}
        />
      </div>

      <LogoLoader open={loading || modalLoading} message={modalLoading ? 'Loading Sale order details...' : loadingMessage} />

      <SaleOrderModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        SaleOrderDetails={SaleOrderDetails}
        setSaleOrderDetails={setSaleOrderDetails}
        onSave={handleSave}
        onVerify={handleVerifySaleOrder}
        onConvert={handleOpenConvertModal}
        customers={customers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
        companyId={companyId}
        branchId={branchId}
        token={token}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Sale Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete Sale order <strong style={{ color: '#1e293b' }}>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stock Warning Dialog — shown when ordered qty exceeds available stock */}
      <Dialog open={stockWarningOpen} onClose={handleStockWarningCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef3c7', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#d97706', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Insufficient Stock</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Some items do not have enough stock to fulfill this order</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            The following items have insufficient stock. You can still proceed with the conversion and adjust quantities in the sale form.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>Item Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>Ordered Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>This Branch</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#dc2626', fontSize: '0.8rem' }}>Shortage</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockWarnings.map((item, idx) => (
                <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 500 }}>{item.name}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#1e293b' }}>{item.ordered}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#16a34a', fontWeight: 500 }}>{item.available}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#dc2626', fontWeight: 600 }}>{item.shortage}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: item.hasBranchStock ? '#d97706' : '#9ca3af' }}>
                    {item.hasBranchStock ? '⚠ Use Branch Split in sale form' : 'No stock in any branch'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleStockWarningCancel} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b' }}>
            Cancel
          </Button>
          <Button onClick={handleStockWarningProceed} variant="contained" sx={{ backgroundColor: '#d97706', '&:hover': { backgroundColor: '#b45309' } }}>
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Sale Confirmation Modal */}
      <Dialog open={convertModalOpen} onClose={handleCloseConvertModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fff7ed', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingCart sx={{ color: '#e67e22', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Convert to Sale</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to convert sale order <strong style={{ color: '#1e293b' }}>{convertItemNo}</strong> to a sale?
          </Typography>
          <Typography sx={{ color: '#e67e22', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Once converted, this sale order cannot be edited or deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseConvertModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b' }}>
            Cancel
          </Button>
          <Button onClick={handleConvertToSale} variant="contained" sx={{ backgroundColor: '#e67e22', '&:hover': { backgroundColor: '#d35400' } }}>
            Convert to Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sale Order Details Section */}
      <Dialog
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ backgroundColor: '#dbeafe', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingCart sx={{ color: '#1e40af', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Sale Order Details
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {selectedDetails?.SaleOrderNo || '-'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDetails} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedDetails && (
            <Box>
              {/* Header Information */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Customer</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{selectedDetails.CustomerName}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{formatDate(selectedDetails.SaleOrderDate)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Status</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selectedDetails.IsVerified ? (
                        <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                      ) : (
                        <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Items Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5 }}>Order Items</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Item</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>Qty</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Rate</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDetails.Details && selectedDetails.Details.length > 0 ? (
                      selectedDetails.Details.map((detail, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {items.find(i => i.Id === detail.ItemId)?.Name || `Item ${detail.ItemId}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              HSN: {detail.HSNCode || '-'} | GST: {detail.GSTRate}%
                            </Typography>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{detail.Quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(detail.TotalAmount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>No items found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>

              {/* Summary */}
              <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Taxable Amount:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(selectedDetails.TaxableAmount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>GST Amount:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(selectedDetails.CGSTAmount + selectedDetails.SGSTAmount + selectedDetails.IGSTAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Freight + Packing:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency((selectedDetails.FreightAmount || 0) + (selectedDetails.PackingAmount || 0) + (selectedDetails.FreightGSTAmount || 0) + (selectedDetails.PackingGSTAmount || 0))}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ borderTop: '2px solid #e2e8f0', mt: 1, pt: 1 }}>
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af' }}>Grand Total:</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(selectedDetails.TotalAmount)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {selectedDetails.Remarks && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fef3c7' }}>
                  <Typography variant="caption" sx={{ color: '#92400e', display: 'block', mb: 0.5, fontWeight: 600 }}>Remarks:</Typography>
                  <Typography variant="body2" sx={{ color: '#78350f' }}>{selectedDetails.Remarks}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={handleCloseDetails}
            variant="outlined"
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' }
            }}
          >
            Close
          </Button>

          {selectedDetails && !selectedDetails.IsVerified && (
            <Button
              onClick={() => {
                handleVerifySaleOrder(selectedDetails.Id, selectedDetails.SaleOrderNo);
                handleCloseDetails();
              }}
              variant="contained"
              startIcon={<CheckCircle />}
              sx={{
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #15803d, #166534)'
                }
              }}
            >
              Verify Sale Order
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* SaleModal — opened when converting SaleOrder to Sale */}
      <SaleModal
        open={saleModalOpen}
        onClose={handleCloseSaleModal}
        modalAction="create"
        formData={saleFormData}
        setFormData={setSaleFormData}
        saleDetails={saleDetails}
        setSaleDetails={setSaleDetails}
        saleBundleDetails={saleBundleDetails}
        setSaleBundleDetails={setSaleBundleDetails}
        saleStockBranchDetails={saleStockBranchDetails}
        onSave={handleSaveSale}
        customers={saleCustomers}
        items={saleItems}
        banks={saleBanks}
        loading={loading}
        freightPackingGST={saleFreightPackingGST}
        companyId={companyId}
        branchId={branchId}
        token={token}
        initialInvoiceTypeId={saleFormData.InvoiceTypeId || null}
        companyData={saleCompanyData}
        onCustomerChange={(masterId) => handleSaleCustomerChange(masterId)}
      />

    </div>
  );
};

export default SaleOrderIndex;

